import React from 'react';
import { makeStyles, Tooltip, Skeleton } from '~/lib/bn-material-ui';
import { useLatestBinLevelImageSrc } from '~/internal/report/tasks/outages/bin-level-image';
import { BinLevel, useLatestLevelFromPageNow } from '../bin-props';
import '~/legacy-lit/js/bins/parts/bin-profile-index';

const useStyles = makeStyles((theme) => ({
  tooltipOuter: {
    pointerEvents: 'none',
    width: 'calc(320px + 100px + 1.75rem)',
    '& .MuiTooltip-tooltip': {
      padding: '0.5rem',
      boxShadow: theme.shadows[4],
      maxWidth: 'unset',
    },
    '& .MuiTooltip-arrow': {
      zIndex: -1,
    },
  },
  tooltipInnerContainer: {
    fontSize: '0.875rem',
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  tooltipContentRow: {
    display: 'flex',
    gap: '0.5rem',
    height: '240px',
  },
  tooltipImageContainer: {
    display: 'flex',
    width: '320px',
    height: '240px',
    overflow: 'hidden',
    position: 'relative',
  },
  tooltipImage: {
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  tooltipImageSkeleton: {
    width: '100%',
    height: '100%',
    margin: 'auto',
  },
  tooltipProfileContainer: {
    display: 'flex',
    width: '100px',
    height: '100%',
    alignItems: 'stretch',
    justifyContent: 'stretch',
  },
  tooltipProfile: {
    '--height-map-width': '100px',
    '--height-map-height': 'calc(100% - 1rem)',
    margin: '-8px', // Accounts for baked in margin in the profile component
  },
  tooltipHeaderRow: {
    display: 'flex',
    fontSize: '1rem',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

/**
 * Pure tooltip component for displaying bin information on hover
 * @param {Object} props - Component props
 * @param {Object} props.hoveredBin - Currently hovered bin data
 * @param {Object} props.tooltipPosition - Position for the tooltip
 * @returns {JSX.Element} Tooltip component
 */
export default function InventoryMapTooltip({
  hoveredBin,
  tooltipPosition,
}) {
  const styles = useStyles();
  const imageSrc = useLatestBinLevelImageSrc({ binId: hoveredBin?.id });
  const { entity: levelEntity } = useLatestLevelFromPageNow({ binHref: hoveredBin?.id });

  if (!hoveredBin) {
    return null;
  }

  return (
    <Tooltip
      classes={{ popper: styles.tooltipOuter }}
      open={!!hoveredBin}
      title={
        <div className={styles.tooltipInnerContainer}>
          <div className={styles.tooltipHeaderRow}>
            <span>{hoveredBin.name}</span>
            <BinLevel binEntity={hoveredBin.entity} />
          </div>

          <div className={styles.tooltipContentRow}>
            <div className={styles.tooltipProfileContainer}>
              {levelEntity?.getSubEntityByRel('https://api.binsentry.com/rel/bin')?.href === hoveredBin.id && levelEntity?.properties?.publishedAt && <bin-profile-index
                href={hoveredBin.id}
                class={styles.tooltipProfile}
                published-at={levelEntity?.properties?.publishedAt}
              />}
            </div>

            <div className={styles.tooltipImageContainer}>
              {imageSrc ? (
                <div className={styles.tooltipImage} style={{ backgroundImage: `url(${imageSrc})` }} />
              ) : (
                <Skeleton variant='rectangular' className={styles.tooltipImageSkeleton} />
              )}
            </div>
          </div>
        </div>
      }
      placement='top'
      arrow
      TransitionProps={{ timeout: 0 }}
      PopperProps={{
        anchorEl: {
          getBoundingClientRect: () => ({
            width: 0,
            height: 0,
            top: tooltipPosition.y,
            left: tooltipPosition.x,
            right: tooltipPosition.x,
            bottom: tooltipPosition.y,
          }),
        },
      }}
    >
      <div />
    </Tooltip>
  );
}
